import React from 'react';
import getLogger from '@ignitesales/logger-es';
import areIntlLocalesSupported from 'intl-locales-supported';
import ReactDOM from 'react-dom';
import WebFont from 'webfontloader';
import deepmerge from 'deepmerge';
import { QuestionTypes, QuestionGroups } from './components/index.mjs';
import default_messages_en from './i18n/index.mjs';
import { AppProvider } from './state/index.mjs';
import { setMessages, getLocale } from './utils/index.mjs';

const logger = getLogger({ component: 'NeuroApplication' });

/**
 * @typedef GuideConfig
 * @property {Object} [customMessages] - custom message overrides. If a key is present, it will be used
 * @property {Object} [htmlMessageValues] - html message values used for parsing formatted messages
 * @property {function} [loginEmployeeFn] - custom login function that overrides default enableLoginFlow function
 * @property {Object} [questionLayouts] - question layout options
 * @property {Object} [questionTypes] - question types
 *
 * @typedef LocaleConfig
 * @property {string} [locale] - current lang locale
 * @property {Array} [supportedLocales] - locales available for app
 */
/**
 *
 * @typedef NeuroInitOptions
 * @property {React.Component} AppComponent - application component
 * @property {React.Component} AppLoadingComponent - loading component shown prior to app render
 * @property {GuideConfig} [guideConfig] - guide specific configuration options
 * @property {HTMLElement} [htmlElement] - DOM element where app will be rendered
 * @property {LocaleConfig} localeConfig - i18n config options for messages, lang, etc.
 * @property {Object} messages - application i18n messages
 * @property {Array} [reducers] - custom reducers for the app
 * @type NeuroInitOptions
 */
const defaultOptions = {
  AppComponent: null,
  AppLoadingComponent: null,
  guideConfig: {
    customMessages: null,
    htmlMessageValues: {},
    loginEmployeeFn: false,
    questionLayouts: QuestionGroups,
    questionTypes: QuestionTypes
  },
  htmlElement: null,
  localeConfig: {
    locale: 'en',
    supportedLocales: ['en']
  },
  messages: null,
  reducers: []
};

/**
 * Neuro application initialization function
 *
 * @param {NeuroInitOptions} options
 * @returns {Promise<boolean>}
 */
const init = async (options) => {
  logger.info('Initializing Ignite Neuro application');

  WebFont.load({
    google: {
      families: ['Source Sans Pro:200,300,300i,400,400i,600,600i,700,700i,900'],
    },
  });

  /**
   *
   * @type {NeuroInitOptions}
   * @private
   */
  const _opts = deepmerge( defaultOptions, options );
  _opts.localeConfig.locale = getLocale(_opts.localeConfig.supportedLocales);

  const locale = _opts.localeConfig.locale;
  const htmlElement = _opts.htmlElement ? _opts.htmlElement : document.getElementById('root');
  const { supportedLocales } = _opts.localeConfig;
  const { questionLayouts, questionTypes } = _opts.guideConfig;

  const bundleMessages = {
    ..._opts.messages,
    en: _opts?.messages?.en
      ? { ...default_messages_en.en, ..._opts.messages.en }
      : default_messages_en.en,
  };

  /* eslint-disable-next-line no-undef */
  await ignite.servicesInitialized();
  /* eslint-disable-next-line no-undef */
  const serverMessages = await ignite.pageResolutionService.loadMessages();
  const messages = {
    [locale]: {
      ...(bundleMessages[locale] || {}),
      ...(serverMessages || {})
    }
  };
  setMessages(messages);

  if (!areIntlLocalesSupported(supportedLocales)) {
    const childLog = logger.child({ supportedLocales });
    childLog.info('importing locale data');
    const promises = [
      import('intl-pluralrules'),
      import('@formatjs/intl-relativetimeformat/polyfill'),
    ];
    supportedLocales.forEach((locale) => {
      promises.push(
        import('@formatjs/intl-relativetimeformat/dist/locale-data/' + locale)
      );
    });

    await Promise.all(promises);
    childLog.info('Completed importing locale data');
  }

  ReactDOM.render(
    <AppProvider
      AppComponent={_opts.AppComponent}
      AppLoadingComponent={_opts.AppLoadingComponent}
      customMessages={_opts.guideConfig.customMessages}
      htmlMessageValues={_opts.guideConfig.htmlMessageValues}
      locale={_opts.localeConfig.locale}
      messages={messages}
      questionGroupTypes={{ ...defaultOptions.guideConfig.questionLayouts, ...questionLayouts }}
      questionTypes={{ ...defaultOptions.guideConfig.questionTypes, ...questionTypes }}
      reducers={_opts.reducers}
    />,
    htmlElement
  );

  logger.info('neuro initialization complete');
};

/**
 * @deprecated since version 3.0
 */
const initializeNeuroApplication = init;

export {
  init as initializeNeuroGuide,
  initializeNeuroApplication
};
export default init;
